import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { DataGrid, type GridColDef, type GridPaginationModel } from '@mui/x-data-grid';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchAudit, auditCsvUrl, type AuditRecord } from '../api/audit';
import { getSocket } from '../api/socket';
import { usePermission } from '../hooks/usePermission';

// ── Result chip ────────────────────────────────────────────────────────────

function ResultChip({ result }: { result: AuditRecord['result'] }) {
  const color =
    result === 'success' ? 'success' :
    result === 'failure' ? 'error'   : 'default';
  return <Chip label={result} color={color} size="small" />;
}

// ── Columns ────────────────────────────────────────────────────────────────

const columns: GridColDef<AuditRecord>[] = [
  {
    field: 'ts',
    headerName: 'Timestamp',
    width: 190,
    valueFormatter: (v: string) =>
      new Date(v).toLocaleString([], { dateStyle: 'short', timeStyle: 'medium' }),
  },
  { field: 'user',   headerName: 'User',   width: 130 },
  { field: 'action', headerName: 'Action', width: 200 },
  { field: 'target', headerName: 'Target', width: 150, valueFormatter: (v?: string) => v ?? '' },
  {
    field: 'result',
    headerName: 'Result',
    width: 110,
    renderCell: (p) => <ResultChip result={p.value as AuditRecord['result']} />,
  },
  { field: 'ip',          headerName: 'IP',          width: 130, valueFormatter: (v?: string) => v ?? '' },
  { field: 'duration_ms', headerName: 'Duration ms', width: 120, valueFormatter: (v?: number) => v != null ? `${v} ms` : '' },
];

// ── Page ───────────────────────────────────────────────────────────────────

export function Audit() {
  const canRead = usePermission('audit.read');
  const qc = useQueryClient();

  const [filterUser,   setFilterUser]   = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterResult, setFilterResult] = useState('');
  const [filterFrom,   setFilterFrom]   = useState('');
  const [filterTo,     setFilterTo]     = useState('');

  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 50 });

  const query = {
    limit:  pagination.pageSize,
    offset: pagination.page * pagination.pageSize,
    ...(filterUser   && { user:   filterUser }),
    ...(filterAction && { action: filterAction }),
    ...(filterResult && { result: filterResult }),
    ...(filterFrom   && { from:   new Date(filterFrom).toISOString() }),
    ...(filterTo     && { to:     new Date(filterTo + 'T23:59:59').toISOString() }),
  };

  const { data, isLoading } = useQuery({
    queryKey: ['audit', query],
    queryFn: () => fetchAudit(query),
    enabled: canRead,
  });

  // Live updates: invalidate whenever a new audit entry arrives.
  useEffect(() => {
    if (!canRead) return;
    const socket = getSocket();
    const handler = () => { void qc.invalidateQueries({ queryKey: ['audit'] }); };
    socket.on('audit:new', handler);
    return () => { socket.off('audit:new', handler); };
  }, [canRead, qc]);

  if (!canRead) {
    return (
      <Box sx={{ mt: 4 }}>
        <Typography color="text.secondary">You do not have permission to view the audit log.</Typography>
      </Box>
    );
  }

  const rows = (data?.entries ?? []).map((e, i) => ({ id: i, ...e }));
  const csvUrl = auditCsvUrl({
    ...(filterUser   && { user:   filterUser }),
    ...(filterAction && { action: filterAction }),
    ...(filterResult && { result: filterResult }),
    ...(filterFrom   && { from:   new Date(filterFrom).toISOString() }),
    ...(filterTo     && { to:     new Date(filterTo + 'T23:59:59').toISOString() }),
  });

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h4">Audit Log</Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<DownloadIcon />}
          href={csvUrl}
          download="audit.csv"
        >
          Export CSV
        </Button>
      </Stack>

      {/* Filters */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }} flexWrap="wrap">
        <TextField
          label="User"
          size="small"
          value={filterUser}
          onChange={(e) => { setFilterUser(e.target.value); setPagination(p => ({ ...p, page: 0 })); }}
          sx={{ minWidth: 140 }}
        />
        <TextField
          label="Action"
          size="small"
          value={filterAction}
          onChange={(e) => { setFilterAction(e.target.value); setPagination(p => ({ ...p, page: 0 })); }}
          sx={{ minWidth: 160 }}
        />
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Result</InputLabel>
          <Select
            label="Result"
            value={filterResult}
            onChange={(e) => { setFilterResult(e.target.value); setPagination(p => ({ ...p, page: 0 })); }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="attempt">attempt</MenuItem>
            <MenuItem value="success">success</MenuItem>
            <MenuItem value="failure">failure</MenuItem>
          </Select>
        </FormControl>
        <TextField
          label="From"
          type="date"
          size="small"
          value={filterFrom}
          onChange={(e) => { setFilterFrom(e.target.value); setPagination(p => ({ ...p, page: 0 })); }}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 155 }}
        />
        <TextField
          label="To"
          type="date"
          size="small"
          value={filterTo}
          onChange={(e) => { setFilterTo(e.target.value); setPagination(p => ({ ...p, page: 0 })); }}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 155 }}
        />
        {(filterUser || filterAction || filterResult || filterFrom || filterTo) && (
          <Button
            size="small"
            onClick={() => {
              setFilterUser('');
              setFilterAction('');
              setFilterResult('');
              setFilterFrom('');
              setFilterTo('');
              setPagination(p => ({ ...p, page: 0 }));
            }}
          >
            Clear
          </Button>
        )}
      </Stack>

      <DataGrid
        rows={rows}
        columns={columns}
        loading={isLoading}
        rowCount={data?.total ?? 0}
        paginationMode="server"
        paginationModel={pagination}
        onPaginationModelChange={setPagination}
        pageSizeOptions={[25, 50, 100]}
        disableRowSelectionOnClick
        sx={{ height: 550 }}
      />
    </Box>
  );
}
